import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useStudio } from "@/contexts/StudioContext";
import { useTranslatedVerticalLabels } from "@/lib/i18n/use-translated-vertical-labels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { STUDIO_ADDRESS_FALLBACK_BY_ID, studioAvatarRowAlign } from "@/lib/studio-display";
import { canUseAllStudiosScope } from "@/lib/studio-scope";

export default function StudioSwitcher() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const labels = useTranslatedVerticalLabels(user?.accountVertical);
  const { activeStudioId, setActiveStudioId, activeStudio } = useStudio();

  const showAllStudiosOption = useMemo(() => {
    if (!canUseAllStudiosScope(user)) return false;
    const p = location.pathname;
    return (
      p === "/dashboard" ||
      p === "/" ||
      p.startsWith("/chats") ||
      p.startsWith("/studio-data") ||
      p.startsWith("/property-data")
    );
  }, [location.pathname, user]);

  const displayName =
    activeStudioId === "all" ? labels.aggregateScopeLabel : (activeStudio?.name ?? labels.selectScopeActionLabel);

  const applyStudioSelection = (id: string) => {
    const p = location.pathname;
    if (p.startsWith("/studio-data") || p.startsWith("/property-data")) {
      const sp = new URLSearchParams(location.search);
      if (id === "all") sp.delete("studio");
      else sp.set("studio", id);
      const q = sp.toString();
      navigate({ pathname: p, search: q ? `?${q}` : "" }, { replace: true });
    }
    setActiveStudioId(id);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-studio-switcher
          className="flex h-9 items-center gap-2 rounded-xl border border-border/60 bg-card pl-4 pr-3.5 text-sm font-semibold leading-5 text-foreground transition-all hover:bg-muted/50"
        >
          {activeStudioId === "all" && showAllStudiosOption && (
            <span className="inline-flex size-[25.6px] shrink-0 items-center justify-center rounded-md bg-gradient-primary text-[9px] font-bold text-primary-foreground">
              ✦
            </span>
          )}
          {activeStudio && (
            <span className="inline-flex size-[25.6px] shrink-0 items-center justify-center rounded-md bg-primary/10 text-[10px] font-bold text-primary">
              {activeStudio.name.charAt(0)}
            </span>
          )}
          <span className="max-w-[140px] truncate leading-5">{displayName}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        alignOffset={0}
        sideOffset={4}
        className="min-w-[240px] w-[min(100vw-1.5rem,320px)] rounded-xl border border-border/50 p-1 shadow-lg"
      >
        {showAllStudiosOption && (
          <>
            <DropdownMenuItem
              onClick={() => applyStudioSelection("all")}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-muted/60"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-primary text-xs font-bold text-primary-foreground">
                ✦
              </span>
              <span className="min-w-0 flex-1 font-semibold">{labels.aggregateScopeLabel}</span>
              {activeStudioId === "all" ? <Check className="h-4 w-4 shrink-0 text-primary" /> : null}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {(user?.studios ?? []).map((studio) => {
          const hasError =
            studio.connections?.instagram?.status === "error" ||
            studio.connections?.whatsapp?.status === "error" ||
            studio.connections?.bookingPlatform?.status === "error";

          const addressLine = studio.address?.trim() || STUDIO_ADDRESS_FALLBACK_BY_ID[studio.id] || "";

          return (
            <DropdownMenuItem
              key={studio.id}
              onClick={() => applyStudioSelection(studio.id)}
              className={cn(
                "flex min-w-0 cursor-pointer gap-3 rounded-lg px-3 py-2.5 hover:bg-muted/60",
                studioAvatarRowAlign(Boolean(addressLine)),
              )}
            >
              <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-bold text-primary">
                {studio.name.charAt(0)}
                {hasError && (
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-destructive" />
                )}
              </span>
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-sm font-semibold text-foreground">{studio.name}</p>
                {addressLine ? (
                  <p className="mt-0.5 truncate text-[11px] leading-snug text-muted-foreground">{addressLine}</p>
                ) : null}
              </div>
              {activeStudioId === studio.id ? (
                <Check className="h-4 w-4 shrink-0 self-center text-primary" aria-hidden />
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
